import { Button, Col, Divider, Flex, Form, Row, Table, TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// components
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import TableTotal from 'components/tableTotal/tableTotal';
import CommitteeMembers from './committeeMembers';
import CommitteeSettings from './committeeSettings';

// services
import { isRTL } from 'core/services/language';
import { onGetCommitteeMeetingsList, onStartCommitteeMeetingAttendance, onStartCommitteeMeetingVoting } from '../services/meetingServices';

// models
import { Committee } from '../models/committee';
import { CommitteeMember } from '../models/members';
import { CommitteeMeeting } from '../models/meetings';

// utils
import { StatusColors } from 'utils/statusColors';
import { dateFormat } from 'utils/dateFormat';

// styles
import styles from '../committees.module.scss';
import { onGetCommitteeMembers } from '../services/memberServices';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import { onExtendCommittee, onTerminateCommittee } from '../services/committeeServices';
import NotificationService from 'core/services/notification';
import VerificationModal from 'modules/main/actions/verificationModal';
import { VerificationPayload } from 'modules/main/actions/commonModels';
import FormElement from 'components/formElement/formElement';
import FormNumberElement from 'components/formNumberElement/formNumberElement';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface CommitteeItemProps {
	committee: Committee;
	refresh?(): void;
	meetingsOnly?: boolean;
}

const CommitteeItem: FunctionComponent<CommitteeItemProps> = ({ committee, refresh, meetingsOnly }) => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const [committeeForm] = Form.useForm();
	const [extendForm] = Form.useForm();

	const [meetingsLoading, setMeetingsLoading] = useState<boolean>(true);
	const [currentMeetingPage, setCurrentMeetingPage] = useState<number>(1);
	const [meetings, setMeetings] = useState<CommitteeMeeting[]>([]);
	const [totalMeetingCount, setTotalMeetingCount] = useState<number>(0);

	const [membersLoading, setMembersLoading] = useState<boolean>(true);
	const [members, setMembers] = useState<CommitteeMember[]>([]);

	const [terminateModalOpen, setTerminateModalOpen] = useState<boolean>(false);
	const [isTerminating, setIsTerminating] = useState<boolean>(false);

	const [extendModalOpen, setExtendModalOpen] = useState<boolean>(false);
	const [isExtending, setIsExtending] = useState<boolean>(false);

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [selectedRequestId, setSelectedRequestId] = useState<number>();
	const [votingModalOpen, setVotingModalOpen] = useState<boolean>(false);
	const [attendanceModalOpen, setAttendanceModalOpen] = useState<boolean>(false);

	const meetingsColumns: TableProps<CommitteeMeeting>['columns'] = [
		{
			title: t('ACTIONS.CODE'),
			dataIndex: 'meeting_number',
			key: 'meeting_number',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status?.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.MEETING_DATE'),
			dataIndex: 'date',
			key: 'date',
			render: (date) => dateFormat(date),
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: 'translated_type',
			key: 'translated_type',
			render: (translated_type) => translated_type?.[isRTL() ? 'ar' : 'en'],
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => (
				<Flex gap={15} justify="center">
					<ListButton type="view" onClick={() => navigate(`/dashboard/actions/boards/committees/${committee.id}/meetings/view/${row.id}`)} />

					{row.status === 'Scheduled' && (
						<ListButton
							type="edit"
							{...suspendedProps}
							onClick={() => navigate(`/dashboard/actions/boards/committees/${committee.id}/meetings/${row.id}`)}
						/>
					)}

					{row.can_open_meeting_attendance && (
						<ListButton
							type="openAttendance"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(row.id);
								setAttendanceModalOpen(true);
							}}
							text={t('ACTIONS.OPEN_ATTENDANCE')}
						/>
					)}

					{row.can_open_voting && (
						<ListButton
							type="vote"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(row.id);
								setVotingModalOpen(true);
							}}
							text={t('ACTIONS.OPEN_VOTING')}
						/>
					)}
				</Flex>
			),
		},
	];

	useEffect(() => {
		getCommitteeMeetings();
		!meetingsOnly && getCommitteeMembers();
	}, []);

	const getCommitteeMeetings = async () => {
		try {
			setMeetingsLoading(true);

			const response = await onGetCommitteeMeetingsList(committee.id, 1);
			setMeetings(response.meetings);
			setTotalMeetingCount(response.pagination.total);

			setMeetingsLoading(false);
		} catch (error) {
			setMeetingsLoading(false);
		}
	};

	const fetchMoreMeetingData = async (page: number) => {
		try {
			setMeetingsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetCommitteeMeetingsList(committee.id, page);
			setMeetings(response.meetings);

			setCurrentMeetingPage(page);
			setMeetingsLoading(false);
		} catch (error) {
			setMeetingsLoading(false);
		}
	};

	const getCommitteeMembers = async () => {
		try {
			setMembersLoading(true);

			const response = await onGetCommitteeMembers(committee.id.toString(), 1);
			setMembers(response.members);

			setMembersLoading(false);
		} catch (error) {
			setMembersLoading(false);
		}
	};

	const openVoting = async () => {
		try {
			setIsSubmitting(true);
			await onStartCommitteeMeetingVoting(committee.id,selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.VOTING_OPENED_SUCCESS'));
			setVotingModalOpen(false);
			setIsSubmitting(false);
			getCommitteeMeetings();
			setSelectedRequestId(undefined);
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.VOTING_OPENED_FAILED'));
		}
	};

	const openAttendance = async () => {
		try {
			setIsSubmitting(true);
			await onStartCommitteeMeetingAttendance(committee.id, selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ATTENDANCE_OPENED_SUCCESS'));
			setAttendanceModalOpen(false);
			setIsSubmitting(false);
			getCommitteeMeetings();
			setSelectedRequestId(undefined);
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ATTENDANCE_OPENED_FAILED'));
		}
	};

	const terminateHandler = async ({ key, method }: VerificationPayload) => {
		try {
			setIsTerminating(true);

			await onTerminateCommittee(committee.id, key, method);

			setIsTerminating(false);
		} catch (error: any) {
			setIsTerminating(false);

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const extendHandler = async () => {
		try {
			const extendFormValues = await extendForm.validateFields(['additional_years']);

			setIsExtending(true);

			await onExtendCommittee(committee.id, extendFormValues.additional_years);

			setIsExtending(false);
			setExtendModalOpen(false);
			extendForm.resetFields();
			refresh && refresh();
		} catch (error: any) {
			if (error?.errorFields) return; // skip form validation errors

			setIsExtending(false);
			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<div className={`${styles.committeeItem}`}>
				<Row gutter={[16, 16]}>
					{!meetingsOnly && (
						<>
							<Col span={24}>
								<Form form={committeeForm} initialValues={{ ...committee, 'name[ar]': committee.name.ar, 'name[en]': committee.name.en }}>
									<CommitteeSettings form={committeeForm} viewOnly />
								</Form>
							</Col>

							<Col span={24}>
								<Divider className="my-2" />
							</Col>

							<Col span={24} className={committee.status === 'draft' ? 'mb-4' : ''}>
								<h6 className="text-primary mb-4">{t('ACTIONS.COMMITTEE_MEMBERS')}</h6>
								<CommitteeMembers
									members={members}
									committeeStatus={committee.status}
									viewOnly
									isLoading={membersLoading}
									canUpdateSecretary={committee.allow_secretary_update_while_active}
									committeeId={committee.id}
									refresh={refresh}
								/>
							</Col>
						</>
					)}

					{committee.status !== 'draft' && (
						<>
							{!meetingsOnly && (
								<Col span={24}>
									<Divider className="mb-2" />
								</Col>
							)}

							<Col span={24}>
								<h6 className="text-primary mb-4">{t('ACTIONS.COMMITTEE_MEETINGS')}</h6>
								<Table
									columns={meetingsColumns}
									dataSource={meetings}
									className="bordered mb-4"
									scroll={{ x: 600 }}
									rowKey="id"
									loading={meetingsLoading}
									pagination={{
										pageSize: 15,
										total: totalMeetingCount,
										showSizeChanger: false,
										current: currentMeetingPage,
										onChange: fetchMoreMeetingData,
										showTotal: (total) => <TableTotal total={total} />,
									}}
									locale={{ emptyText: <NoData /> }}
									sticky
								/>
							</Col>
						</>
					)}

					{committee.status === 'active' && (
						<Col span={24} className="my-5">
							<Flex align="center" justify="flex-end" gap={20}>
								<SuspendedTooltip>
									<Button type="primary" size="middle" onClick={() => setTerminateModalOpen(true)} danger>
										{t('ACTIONS.TERMINATE')}
									</Button>
								</SuspendedTooltip>

								<SuspendedTooltip>
									<Button type="primary" size="middle" onClick={() => setExtendModalOpen(true)}>
										{t('ACTIONS.EXTEND')}
									</Button>
								</SuspendedTooltip>
							</Flex>
						</Col>
					)}
				</Row>
			</div>

			{/* VOTING MODAL */}
			<ConfirmModalComponent
				open={votingModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={openVoting}
				isLoading={isSubmitting}
				onDiscard={() => setVotingModalOpen(false)}
				title={t('ACTIONS.OPEN_VOTING')}
				description={t('ACTIONS.CONFIRM_OPEN_VOTING')}
				descriptionClasses="text-center"
			/>

			{/* ATTENDANCE MODAL */}
			<ConfirmModalComponent
				open={attendanceModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={openAttendance}
				isLoading={isSubmitting}
				onDiscard={() => setAttendanceModalOpen(false)}
				title={t('ACTIONS.OPEN_ATTENDANCE')}
				description={t('ACTIONS.CONFIRM_OPEN_ATTENDANCE')}
				descriptionClasses="text-center"
			/>

			{/* otp verification modal for termination */}
			<VerificationModal
				id={committee.id.toString()}
				open={terminateModalOpen}
				isLoading={isTerminating}
				onCancel={() => setTerminateModalOpen(false)}
				onVerificationSuccess={terminateHandler}
				module="terminate_committee"
			/>

			{/* extend committee modal */}
			<ConfirmModalComponent
				open={extendModalOpen}
				onDiscard={() => {
					setExtendModalOpen(false);
					extendForm.resetFields();
				}}
				onConfirm={extendHandler}
				confirmText={t('COMMON.CONFIRM')}
				title={t('ACTIONS.EXTEND_COMMITTEE')}
				isLoading={isExtending}>
				<Form form={extendForm} className="mt-3">
					<FormNumberElement
						fullWidth
						min={1}
						name="additional_years"
						label={t('ACTIONS.ADDITIONAL_YEARS')}
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
					/>
				</Form>
			</ConfirmModalComponent>
		</>
	);
};

export default CommitteeItem;
